import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Avatar,
  AvatarGroup,
} from '@mui/material';
import {
  Group as GroupIcon,
  Lock as LockIcon,
  Public as PublicIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../i18n';
import { LearningGroup } from '../../services/learningGroupService';

interface GroupCardProps {
  group: LearningGroup;
  onJoin?: () => void;
  onLeave?: () => void;
  onView?: () => void;
  loading?: boolean;
}

const GroupCard: React.FC<GroupCardProps> = ({
  group,
  onJoin,
  onLeave,
  onView,
  loading = false,
}) => {
  const { t } = useTranslation();

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <GroupIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" noWrap>
              {group.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {group.isPublic ? (
                <PublicIcon fontSize="small" color="action" />
              ) : (
                <LockIcon fontSize="small" color="action" />
              )}
              <Typography variant="caption" color="text.secondary">
                {group.isPublic
                  ? t('learningGroups.public', 'Öffentlich')
                  : t('learningGroups.private', 'Privat')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {group.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {group.description}
          </Typography>
        )}

        {group.courseTitle && (
          <Chip
            label={group.courseTitle}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ mb: 1 }}
          />
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AvatarGroup max={3} sx={{ '& .MuiAvatar-root': { width: 24, height: 24, fontSize: '0.75rem' } }}>
              {[...Array(Math.min(group.memberCount, 3))].map((_, i) => (
                <Avatar key={i} sx={{ bgcolor: `hsl(${i * 120}, 70%, 50%)` }}>
                  {String.fromCharCode(65 + i)}
                </Avatar>
              ))}
            </AvatarGroup>
            <Typography variant="caption" color="text.secondary">
              {t('learningGroups.members', '%{count} Mitglieder', { count: group.memberCount })}
            </Typography>
          </Box>
          {group.maxMembers && (
            <Chip
              label={group.isFull ? t('learningGroups.full', 'Voll') : `${group.memberCount}/${group.maxMembers}`}
              size="small"
              color={group.isFull ? 'error' : 'default'}
            />
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
        {group.isMember ? (
          <>
            <Button size="small" onClick={onView}>
              {t('learningGroups.open', 'Öffnen')}
            </Button>
            {!group.isAdmin && (
              <Button size="small" color="error" onClick={onLeave} disabled={loading}>
                {t('learningGroups.leave', 'Austreten')}
              </Button>
            )}
          </>
        ) : (
          <Button
            size="small"
            variant="contained"
            onClick={onJoin}
            disabled={loading || group.isFull || !group.isPublic}
          >
            {t('learningGroups.join', 'Beitreten')}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default GroupCard;
